import React, { useEffect, useRef, useState } from "react";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { PageContent } from "../../components/PageContent/PageContent";
import { useDataApiClient } from "../../hooks/useApiClient";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  ButtonGroup,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Select,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { Divider, HStack, VStack } from "@chakra-ui/layout";
import { CherryHubDataApi } from "../../api/dataApiModels";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  EditIcon,
  RepeatIcon,
} from "@chakra-ui/icons";
import { useToast } from "../../hooks/useToast";
import { DocumentTime } from "../../styles/icons";

export const Configurations = () => {
  const isMounted = useRef(true);
  const [config, setConfig] = useState<CherryHubDataApi.Configurations>();
  const [logLevel, setLogLevel] = useState<string>();
  const [inputLogLevel, setInputLogLevel] = useState("");
  const apiClient = useDataApiClient();
  const { showSuccessToast } = useToast();

  const setConfiguration = async () => {
    const result = await apiClient.getConfigurations();
    if (result.ok && isMounted.current) {
      setConfig(result.data);
    }
  };

  const reloadConfig = async () => {
    const result = await apiClient.reloadConfigurations();
    if (result.ok) {
      setConfig(undefined);
      setConfiguration();
      showSuccessToast(result.data?.Value as string);
    }
  };

  const validateConfig = async () => {
    const result = await apiClient.validateConfigurations();
    if (result.ok) {
      showSuccessToast(result.data?.Value as string);
    }
  };

  const refreshDocumentCache = async () => {
    const result = await apiClient.refreshDocumentCache();
    if (result.ok) {
      showSuccessToast(result.data?.Value as string);
    }
  };

  const setLogEventLevel = async () => {
    const result = await apiClient.getLogEventLevel();
    if (result.ok && isMounted.current) {
      setLogLevel(result.data?.Value as string);
      setInputLogLevel(result.data?.Value as string);
    }
  };

  const changeLogEventLevel = async () => {
    const result = await apiClient.setLogEventLevel(inputLogLevel);
    if (result.ok) {
      setLogLevel(result.data?.Value as string);
      showSuccessToast("Log Event Level Changed OK");
    }
  };

  const resetLogEventLevel = async () => {
    const result = await apiClient.resetLogEventLevel();
    if (result.ok) {
      setLogLevel(result.data?.Value as string);
      showSuccessToast("Log Event Level Reseted OK");
    }
  };

  useEffect(() => {
    setConfiguration();
  }, [isMounted]);

  useEffect(() => {
    setLogEventLevel();
  }, [isMounted, logLevel]);

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Configuration</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack width="100%">
          <ButtonGroup variant="solid" width="100%">
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                colorScheme="cherryButton"
              >
                Actions
              </MenuButton>
              <MenuList>
                <MenuItem icon={<RepeatIcon />} onClick={reloadConfig}>
                  Reload
                </MenuItem>
                <MenuItem icon={<CheckCircleIcon />} onClick={validateConfig}>
                  Validate
                </MenuItem>
              </MenuList>
            </Menu>
            <Button
              leftIcon={<DocumentTime />}
              colorScheme="cherryButton"
              onClick={refreshDocumentCache}
            >
              Document-Cache Refresh
            </Button>
          </ButtonGroup>
          {(config && (
            <Box width="100%" overflowX="auto">
              <ConfigurationDetail config={config} />
            </Box>
          )) || (
            <Box width="100%" padding="6" boxShadow="lg">
              <Skeleton height="40px" />
              <SkeletonText mt="4" noOfLines={4} spacing="4" />
              <Skeleton height="40px" />
              <SkeletonText mt="4" noOfLines={4} spacing="4" />
              <Skeleton height="40px" />
              <SkeletonText mt="4" noOfLines={4} spacing="4" />
            </Box>
          )}
          <br />

          <Box alignSelf="flex-start">
            <h1>
              <b>Logging</b>
            </h1>
          </Box>
          <Divider />
          <Box alignSelf="flex-start" width="100%">
            <Text>
              <b>Log Event Level</b> : {logLevel}
            </Text>
            <Select
              width="300px"
              value={inputLogLevel}
              onChange={(e) => setInputLogLevel(e.target.value)}
            >
              <option value="Verbose">Verbose</option>
              <option value="Debug">Debug</option>
              <option value="Information">Information</option>
              <option value="Warning">Warning</option>
              <option value="Error">Error</option>
              <option value="Fatal">Fatal</option>
            </Select>
          </Box>
          <HStack w="100%">
            <Button
              leftIcon={<EditIcon />}
              colorScheme="cherryButton"
              onClick={changeLogEventLevel}
            >
              Change Log Level
            </Button>
            <Button
              leftIcon={<RepeatIcon />}
              colorScheme="cherryButton"
              onClick={resetLogEventLevel}
            >
              Reset
            </Button>
          </HStack>
        </VStack>
      </PageContent>
    </>
  );
};

interface ConfigurationDetailProps {
  config: CherryHubDataApi.Configurations | undefined;
}

export const ConfigurationDetail = (props: ConfigurationDetailProps) => {
  return (
    <Box alignSelf="flex-start">
      {props.config &&
        Object.entries(props.config).map(([ckey, conf]) => {
          return (
            <Accordion key={ckey} allowToggle>
              <AccordionItem>
                <h2>
                  <AccordionButton
                    _expanded={{ bg: "cherryButtonDark", color: "grey" }}
                  >
                    <Box flex="1" textAlign="left">
                      {ckey}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel width="100%">
                  <TableContainer
                    alignSelf="start"
                    width="100%"
                    overflowX="auto"
                  >
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Field</Th>
                          <Th>Value</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <ConfigurationRow key={ckey} configs={conf} />
                      </Tbody>
                    </Table>
                  </TableContainer>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          );
        })}
    </Box>
  );
};

interface ConfigurationRowProps {
  configs: CherryHubDataApi.ConfigurationRow[];
}

export const ConfigurationRow = (props: ConfigurationRowProps) => {
  return (
    <>
      {props.configs?.map((row, i) => {
        return (
          <Tr key={i}>
            <Td>{row.Key}</Td>
            <Td>{row.Value}</Td>
          </Tr>
        );
      })}
    </>
  );
};
